import React from 'react';
import { Plus, MessageSquare, Share2, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ActionButtonType = 'add' | 'quote' | 'share' | 'find';

interface ActionButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
  size?: 'default' | 'large';
}

export const ActionButton = ({
  icon,
  label,
  onClick,
  active = false,
  variant = 'primary',
  disabled = false,
  size = 'default'
}: ActionButtonProps) => {
  return (
    <button
      onClick={onClick}
      title={variant === 'secondary' ? label : undefined}
      aria-label={label}
      disabled={disabled}
      className={cn(
        "flex items-center justify-center rounded-md transition-colors flex-1",
        size === 'large' ? "h-16" : "h-9",
        variant === 'primary'
          ? active
            ? "bg-purple-100 text-purple-900 border-purple-300 border"
            : "bg-purple-50 hover:bg-purple-100 text-purple-800 border border-purple-200"
          : active
            ? "bg-slate-100 text-slate-900 border-slate-300 border"
            : "bg-slate-50 hover:bg-slate-100 text-slate-800 border border-slate-200",
        variant === 'primary'
          ? size === 'large'
            ? "px-2"
            : "px-4"
          : size === 'large'
            ? "w-12"
            : "w-9",
        disabled && "opacity-50 cursor-not-allowed hover:bg-purple-50 hover:bg-slate-50"
      )}
    >
      <div className="flex items-center">
        {icon}
        {variant === 'primary' && (
          <span className={cn(
            "font-medium ml-2",
            size === 'large' ? "text-base" : "text-sm"
          )}>
            {label}
          </span>
        )}
      </div>
    </button>
  );
};

interface ActionButtonsProps {
  activeButton: ActionButtonType | null;
  onButtonClick: (button: ActionButtonType) => void;
  hasTradespeople?: boolean;
  size?: 'default' | 'large';
  layout?: 'default' | 'stacked';
}

export const ActionButtons = ({
  activeButton,
  onButtonClick,
  hasTradespeople = true,
  size = 'default',
  layout = 'default'
}: ActionButtonsProps) => {
  const isPrimaryStacked = layout === 'stacked';

  return (
    <div className={cn(
      isPrimaryStacked
        ? "flex flex-col space-y-3"
        : "flex items-center gap-2 w-full"
    )}>
      <div className={cn(
        "flex w-full",
        isPrimaryStacked
          ? "gap-3"
          : "gap-2"
      )}>
        <ActionButton
          icon={<Plus className={size === 'large' ? "h-5 w-5" : "h-4 w-4"} />}
          label="Add New"
          onClick={() => onButtonClick('add')}
          active={activeButton === 'add'}
          variant="primary"
          size={size}
        />
        <ActionButton
          icon={<MessageSquare className={size === 'large' ? "h-5 w-5" : "h-4 w-4"} />}
          label="SmartQuote"
          onClick={() => onButtonClick('quote')}
          active={activeButton === 'quote'}
          variant="primary"
          disabled={!hasTradespeople}
          size={size}
        />
      </div>

      {/* Secondary buttons removed - now handled by UtilityRow */}
    </div>
  );
};
